import { motion } from "framer-motion";
import { useCollection } from "@/hooks/useCollection";
import { Reveal } from "./Reveal";

const EASE = [0.22, 1, 0.36, 1];

function MemberCard({ m, i }) {
  const initials = m.name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: EASE }}
      className="group overflow-hidden rounded-[10px] border border-line bg-surface shadow-[0_10px_30px_rgba(18,33,27,0.04)] transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(18,33,27,0.12)]"
      data-testid={`board-member-${i}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-bone">
        {m.photo ? (
          <img
            src={m.photo}
            alt={m.name}
            loading="lazy"
            className="img-editorial h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-display text-5xl font-semibold text-pine/40">
            {initials}
          </div>
        )}
        <img src="/brand/leaf.png" alt="" aria-hidden className="absolute right-3 top-3 h-7 w-7 object-contain opacity-80" />
      </div>
      <div className="p-5">
        <h4 className="font-display text-lg font-semibold text-ink">{m.name}</h4>
        <p className="mt-1 font-display text-xs font-semibold uppercase tracking-[0.14em] text-goldtext">{m.role}</p>
        {m.institution && <p className="mt-1 text-sm text-muted">{m.institution}</p>}
        {m.bio && <p className="mt-3 text-[0.95rem] leading-relaxed text-body">{m.bio}</p>}
      </div>
    </motion.article>
  );
}

export default function BoardSection({ title, text }) {
  const { items, visible, loading } = useCollection("board");
  if (loading || !visible) return null;
  const seats = Math.max(0, 9 - items.length);

  return (
    <Reveal
      delay={0.15}
      className="relative overflow-hidden rounded-[10px] border border-line bg-bone p-8 md:col-span-8 md:p-12"
      data-testid="board-section"
    >
      <p className="font-display text-xs font-semibold uppercase tracking-[0.16em] text-goldtext">Founding board</p>
      <h3 className="mt-3 font-display text-2xl font-semibold leading-tight text-ink md:text-4xl" data-testid="board-title">
        {title}
      </h3>
      <p className="mt-5 max-w-2xl leading-relaxed text-body" data-testid="board-text">
        {text}
      </p>

      {items.length > 0 && (
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="board-grid">
          {items.map((m, i) => (
            <MemberCard key={m.id} m={m} i={i} />
          ))}
        </div>
      )}

      {seats > 0 && (
        <div className="mt-8">
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-9">
            {Array.from({ length: seats }).map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.6 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.05, duration: 0.5, ease: EASE }}
                className="flex aspect-square items-center justify-center rounded-md border border-dashed border-moss/40 bg-surface"
                aria-hidden
              >
                <img src="/brand/leaf.png" alt="" className="h-5 w-5 object-contain opacity-50" />
              </motion.div>
            ))}
          </div>
          <p className="mt-3 text-sm text-body" data-testid="board-seats-note">
            {seats} of 9 seats to be announced as confirmed.
          </p>
        </div>
      )}
    </Reveal>
  );
}
